import {Autocomplete, TextField, FormControl, FormHelperText} from '@mui/material';
import {FC, memo, useCallback} from 'react';
import {Controller, Message, useFormContext, ValidationRule} from 'react-hook-form';
import {theme} from '../../../../styles/theme';
import {DropdownOption} from './form-types';
import {KeyboardArrowDown} from '@mui/icons-material';
import {localized} from 'i18n/i18n';

interface Props {
  name: string;
  label?: string;
  options: DropdownOption[];
  disabled?: boolean;
  noDefault?: boolean;
  validators?: Partial<{
    required: Message | ValidationRule<boolean>;
    disabled: boolean;
  }>;
}

export const SelectSearchInput: FC<Props> = memo((props) => {
  const {
    control,
    formState: {errors},
  } = useFormContext();

  const error = errors[props.name];

  const getOptionLabel = useCallback((option: DropdownOption) => option.label ?? '', []);

  const getSelectedOption = useCallback(
    (value) => {
      return props.options.find((opt) => opt.value === value);
    },
    [props.options],
  );

  const handleAutocompleteChange = useCallback(
    (fieldOnChange: (...event: any[]) => void) => (_event: React.SyntheticEvent, newValue: DropdownOption | null) => {
      fieldOnChange(newValue ? newValue.value : '');
    },
    [],
  );

  const isOptionEqualToValue = useCallback((option: DropdownOption, value: DropdownOption | string | number) => {
    if (!value) return false;
    return option.value === (typeof value === 'object' ? value.value : value);
  }, []);

  if (props.options.length === 0) return null;
  return (
    <Controller
      name={props.name}
      control={control}
      rules={props.validators}
      render={({field}) => {
        return (
          <FormControl sx={{marginTop: 1, marginBottom: 2, flex: 1, display: 'flex'}}>
            <Autocomplete
              {...field}
              options={props.options}
              disabled={props.disabled}
              getOptionLabel={getOptionLabel}
              value={getSelectedOption(field.value) ?? (props.noDefault ? null : props.options[0])}
              onChange={handleAutocompleteChange(field.onChange)}
              isOptionEqualToValue={isOptionEqualToValue}
              popupIcon={<KeyboardArrowDown />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={props.label}
                  error={!!error}
                  placeholder={localized('TypeToSearch')}
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      backgroundColor: theme.palette.common.white,
                      paddingRight: theme.spacing(1),
                      paddingLeft: theme.spacing(1),
                      color: theme.palette.text.primary,
                      marginLeft: '-6px',
                    },
                  }}
                />
              )}
            />
            {!!error && (
              <FormHelperText sx={{color: theme.palette.error.main}}>{error.message as string}</FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
});
